/**
 * Branding Store
 *
 * Manages branding configuration state for the platform.
 * Fetches branding on app load and applies colors/favicon dynamically.
 */

import { create } from 'zustand'
import { BRANDING } from '@/config/brandingDefaults'
import { api } from '@/lib/api'

// Types
export interface BrandingLogos {
  light: string | null
  dark: string | null
  icon: string | null
  favicon: string | null
}

export interface BrandingColors {
  primary: string
  secondary: string
  sidebarBg: string
  sidebarText: string
}

export interface LoginFeature {
  title: string
  description: string | null
}

export interface LoginConfig {
  title: string
  subtitle: string | null
  description: string | null
  backgroundImage: string | null
  features: LoginFeature[]
}

export interface FooterConfig {
  companyName: string
  supportEmail: string | null
  supportUrl: string | null
  showPoweredBy: boolean
  poweredByText: string
}

export interface BrandingConfig {
  appName: string
  tagline: string | null
  logos: BrandingLogos
  colors: BrandingColors
  login: LoginConfig
  footer: FooterConfig
  updatedAt: string
}

// Default branding (sourced from brandingDefaults.ts)
export const DEFAULT_BRANDING: BrandingConfig = {
  appName: BRANDING.appName,
  tagline: BRANDING.tagline,
  logos: {
    light: null,
    dark: null,
    icon: null,
    favicon: null,
  },
  colors: { ...BRANDING.colors },
  login: {
    title: BRANDING.login.title,
    subtitle: BRANDING.login.subtitle,
    description: BRANDING.login.description,
    backgroundImage: null,
    features: [...BRANDING.login.features],
  },
  footer: { ...BRANDING.footer },
  updatedAt: new Date().toISOString(),
}

interface BrandingState {
  // State
  branding: BrandingConfig
  isLoading: boolean
  isSaving: boolean
  error: string | null
  isInitialized: boolean

  // Actions
  fetchBranding: () => Promise<void>
  fetchPublicBranding: () => Promise<void>
  updateBranding: (updates: Partial<BrandingUpdatePayload>) => Promise<void>
  resetToDefaults: () => Promise<void>
  uploadLogo: (type: 'light' | 'dark' | 'icon' | 'favicon', file: File) => Promise<string>
  deleteLogo: (type: 'light' | 'dark' | 'icon' | 'favicon') => Promise<void>
  uploadLoginBackground: (file: File) => Promise<string>
  deleteLoginBackground: () => Promise<void>
  applyBranding: () => void
  clearError: () => void
}

// Update payload type (flat structure for API)
export interface BrandingUpdatePayload {
  appName?: string
  tagline?: string
  primaryColor?: string
  secondaryColor?: string
  sidebarBgColor?: string
  sidebarTextColor?: string
  loginTitle?: string
  loginSubtitle?: string
  loginDescription?: string
  loginFeature1Title?: string
  loginFeature1Description?: string
  loginFeature2Title?: string
  loginFeature2Description?: string
  loginFeature3Title?: string
  loginFeature3Description?: string
  footerCompanyName?: string
  supportEmail?: string
  supportUrl?: string
  showPoweredBy?: boolean
  poweredByText?: string
}

export const useBrandingStore = create<BrandingState>((set, get) => ({
  // Initial state
  branding: DEFAULT_BRANDING,
  isLoading: false,
  isSaving: false,
  error: null,
  isInitialized: false,

  // Fetch branding (admin - requires auth)
  fetchBranding: async () => {
    set({ isLoading: true, error: null })

    try {
      const response = await api.get<BrandingConfig>('/admin/branding')
      set({
        branding: response.data,
        isLoading: false,
        isInitialized: true,
      })
      get().applyBranding()
    } catch (error) {
      console.error('Failed to fetch branding:', error)
      set({
        isLoading: false,
        error: 'Failed to load branding configuration',
      })
    }
  },

  // Fetch public branding (no auth - for login page)
  fetchPublicBranding: async () => {
    set({ isLoading: true, error: null })

    try {
      const response = await api.get<BrandingConfig>('/admin/branding/public')
      set({
        branding: { ...DEFAULT_BRANDING, ...response.data },
        isLoading: false,
        isInitialized: true,
      })
      get().applyBranding()
    } catch (error) {
      console.error('Failed to fetch public branding:', error)
      // Use defaults on error
      set({
        branding: DEFAULT_BRANDING,
        isLoading: false,
        isInitialized: true,
      })
      get().applyBranding()
    }
  },

  // Update branding
  updateBranding: async (updates) => {
    set({ isSaving: true, error: null })

    try {
      const response = await api.put<BrandingConfig>('/admin/branding', updates)
      set({
        branding: response.data,
        isSaving: false,
      })
      get().applyBranding()
    } catch (error) {
      console.error('Failed to update branding:', error)
      set({
        isSaving: false,
        error: 'Failed to save branding configuration',
      })
      throw error
    }
  },

  // Reset to defaults
  resetToDefaults: async () => {
    set({ isSaving: true, error: null })

    try {
      await api.post('/admin/branding/reset')
      set({
        branding: DEFAULT_BRANDING,
        isSaving: false,
      })
      get().applyBranding()
    } catch (error) {
      console.error('Failed to reset branding:', error)
      set({
        isSaving: false,
        error: 'Failed to reset branding',
      })
      throw error
    }
  },

  // Upload logo
  uploadLogo: async (type, file) => {
    const formData = new FormData()
    formData.append('file', file)

    try {
      const response = await api.post<{ fileUrl: string }>(`/admin/branding/logo/${type}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })

      // Update local state
      const { branding } = get()
      set({
        branding: {
          ...branding,
          logos: {
            ...branding.logos,
            [type]: response.data.fileUrl,
          },
        },
      })

      return response.data.fileUrl
    } catch (error) {
      console.error(`Failed to upload ${type} logo:`, error)
      throw error
    }
  },

  // Delete logo
  deleteLogo: async (type) => {
    try {
      await api.delete(`/admin/branding/logo/${type}`)

      // Update local state
      const { branding } = get()
      set({
        branding: {
          ...branding,
          logos: {
            ...branding.logos,
            [type]: null,
          },
        },
      })
    } catch (error) {
      console.error(`Failed to delete ${type} logo:`, error)
      throw error
    }
  },

  // Upload login background
  uploadLoginBackground: async (file) => {
    const formData = new FormData()
    formData.append('file', file)

    try {
      const response = await api.post<{ fileUrl: string }>('/admin/branding/login-background', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })

      // Update local state
      const { branding } = get()
      set({
        branding: {
          ...branding,
          login: {
            ...branding.login,
            backgroundImage: response.data.fileUrl,
          },
        },
      })

      return response.data.fileUrl
    } catch (error) {
      console.error('Failed to upload login background:', error)
      throw error
    }
  },

  // Delete login background
  deleteLoginBackground: async () => {
    try {
      await api.delete('/admin/branding/login-background')

      // Update local state
      const { branding } = get()
      set({
        branding: {
          ...branding,
          login: {
            ...branding.login,
            backgroundImage: null,
          },
        },
      })
    } catch (error) {
      console.error('Failed to delete login background:', error)
      throw error
    }
  },

  // Apply branding to DOM
  applyBranding: () => {
    const { branding } = get()

    // Apply CSS variables for colors
    const root = document.documentElement
    root.style.setProperty('--brand-primary', branding.colors.primary)
    root.style.setProperty('--brand-secondary', branding.colors.secondary)
    root.style.setProperty('--brand-sidebar-bg', branding.colors.sidebarBg)
    root.style.setProperty('--brand-sidebar-text', branding.colors.sidebarText)

    // Convert hex to HSL for Tailwind/shadcn compatibility
    const hexToHSL = (hex: string): string => {
      const r = parseInt(hex.slice(1, 3), 16) / 255
      const g = parseInt(hex.slice(3, 5), 16) / 255
      const b = parseInt(hex.slice(5, 7), 16) / 255

      const max = Math.max(r, g, b)
      const min = Math.min(r, g, b)
      let h = 0
      let s = 0
      const l = (max + min) / 2

      if (max !== min) {
        const d = max - min
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
        switch (max) {
          case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break
          case g: h = ((b - r) / d + 2) / 6; break
          case b: h = ((r - g) / d + 4) / 6; break
        }
      }

      return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`
    }

    // Apply HSL values for Tailwind
    root.style.setProperty('--primary', hexToHSL(branding.colors.primary))

    // Update favicon if custom one exists
    if (branding.logos.favicon) {
      const favicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
      if (favicon) {
        favicon.href = branding.logos.favicon
      }
    }

    // Update document title
    document.title = branding.appName
  },

  clearError: () => set({ error: null }),
}))

// Selector hooks
export const useAppName = () => useBrandingStore((state) => state.branding.appName)
export const useTagline = () => useBrandingStore((state) => state.branding.tagline)
export const useBrandingColors = () => useBrandingStore((state) => state.branding.colors)
export const useBrandingLogos = () => useBrandingStore((state) => state.branding.logos)
export const useLoginConfig = () => useBrandingStore((state) => state.branding.login)
export const useFooterConfig = () => useBrandingStore((state) => state.branding.footer)
